import { useEffect } from "react";
import { short } from "../../utils/Constant";

interface IFinancialYearCycleProps {
  selectedYear: string;
  setSelectedYear: (year: string) => void;
  startMonth: number;
  startDateValue: number;
  endMonth: number;
  endDateValue: number;
  selectClassName?: string;
  name?: string;
}

const generateFinancialYears = (
  baseYear: number,
  rangeBefore: number = 2,
  rangeAfter: number = 2
): string[] => {
  const years: string[] = [];
  for (let i = -rangeBefore; i <= rangeAfter; i++) {
    const from = baseYear + i;
    const to = from + 1; // always next year
    years.push(`${from}-${to}`);
  }
  return years;
};

const getCurrentFinancialYear = (
  startMonth: number,
  startDateValue: number,
  endMonth: number,
  endDateValue: number
): string => {
  const today = new Date();
  const currentYear = today.getFullYear();

  const fyStart = new Date(currentYear, startMonth, startDateValue); // e.g. 1 Oct 2025
  const fyEnd = new Date(currentYear + 1, endMonth, endDateValue); // e.g. 30 Sep 2026

  if (today < fyStart) {
    // before Oct → belongs to last FY
    return `${currentYear - 1}-${currentYear}`;
  } else if (today > fyEnd) {
    // after Sep → belongs to next FY
    return `${currentYear + 1}-${currentYear + 2}`;
  } else {
    return `${currentYear}-${currentYear + 1}`;
  }
};

const getBaseFinancialYear = (startMonth: number): number => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  // If current month < startMonth, FY started last year
  return month < startMonth ? year - 1 : year;
};

const FinancialYearCycle = ({
  selectedYear,
  setSelectedYear,
  startMonth,
  startDateValue,
  endMonth,
  endDateValue,
  selectClassName,
  name = "name",
}: IFinancialYearCycleProps) => {
  const baseYear = getBaseFinancialYear(startMonth);
  const years = generateFinancialYears(baseYear, 3, 0);

  useEffect(() => {
    if (!selectedYear) {
      const autoSelectedYear = getCurrentFinancialYear(
        startMonth,
        startDateValue,
        endMonth,
        endDateValue
      );
      setSelectedYear(autoSelectedYear);
    }
  }, [
    selectedYear,
    startMonth,
    startDateValue,
    endMonth,
    endDateValue,
    setSelectedYear,
  ]);

  const handleOnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(event.target.value);
  };

  return (
    <select
      name={name}
      className={`p-2 border-1 b-r-6 f-12 pointer ${selectClassName}`}
      value={selectedYear}
      onChange={handleOnChange}
      style={{
        border: "1px solid #dee2e6",
        background: "#f2f5fa",
        color: "#0C6AF3",
      }}
    >
      {years.map((fy) => {
        const [from, to] = fy.split("-").map(Number);

        const startMonthLabel = new Date(from, startMonth).toLocaleString(
          "default",
          { month: short }
        ); // Oct
        const endMonthLabel = new Date(to, endMonth).toLocaleString("default", {
          month: short,
        }); // Sep


        const label = `${startMonthLabel} ${from} - ${endMonthLabel} ${to}`;
        return (
          <option className="text-black" key={fy} value={fy}>
            {label}
          </option>
        );
      })}
    </select>
  );
};

export default FinancialYearCycle;
